/**
 * playerState.ts - Cloud sync for user game data
 *
 * Syncs localStorage data (game lists, player roles) to DynamoDB via Cognito user ID
 */

import { API, Auth } from "aws-amplify";
import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api-graphql";
import * as mutations from "../graphql/mutations";
import * as queries from "../graphql/queries";

export interface PlayerSnapshot {
  gameList?: string[];
  playerAssignments?: Record<string, number>;
  preferences?: any;
}

export interface PlayerStateRecord {
  id: string;
  gameList?: string | null;
  playerAssignments?: string | null;
  preferences?: string | null;
}

/** Check if user is authenticated */
export const isAuthenticated = async (): Promise<boolean> => {
  try {
    await Auth.currentAuthenticatedUser();
    return true;
  } catch {
    return false;
  }
};

/** Get Cognito user ID for PlayerState primary key */
export const getPlayerStateId = async (): Promise<string | null> => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    return user.username;
  } catch {
    return null;
  }
};

/** Capture current localStorage state */
export const captureLocalSnapshot = (): PlayerSnapshot => {
  const snapshot: PlayerSnapshot = {};

  // Capture game list
  const gameListStr = localStorage.getItem("gameList");
  if (gameListStr) {
    try {
      snapshot.gameList = JSON.parse(gameListStr);
    } catch {}
  }

  // Capture player assignments (which player am I in each game?)
  const playerAssignments: Record<string, number> = {};
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith("game-") && key.endsWith("-player")) {
      const gameId = key.replace("game-", "").replace("-player", "");
      const playerNum = localStorage.getItem(key);
      if (playerNum) {
        playerAssignments[gameId] = parseInt(playerNum, 10);
      }
    }
  });
  if (Object.keys(playerAssignments).length > 0) {
    snapshot.playerAssignments = playerAssignments;
  }

  return snapshot;
};

/** Apply snapshot to localStorage */
export const applySnapshotToLocalStorage = (snapshot: PlayerSnapshot): void => {
  if (snapshot.gameList) {
    localStorage.setItem("gameList", JSON.stringify(snapshot.gameList));
  }

  if (snapshot.playerAssignments) {
    Object.entries(snapshot.playerAssignments).forEach(
      ([gameId, playerNum]) => {
        localStorage.setItem(`game-${gameId}-player`, playerNum.toString());
      }
    );
  }
};

/** Merge local and remote snapshots (union of game lists, remote wins for conflicts) */
export const mergeSnapshots = (
  local: PlayerSnapshot,
  remote: PlayerSnapshot
): PlayerSnapshot => {
  const merged: PlayerSnapshot = {};

  // Merge game lists (union)
  const localGames = new Set(local.gameList || []);
  const remoteGames = new Set(remote.gameList || []);
  const allGames = new Set<string>();
  localGames.forEach((g) => allGames.add(g));
  remoteGames.forEach((g) => allGames.add(g));
  merged.gameList = Array.from(allGames);

  // Merge player assignments (remote wins)
  merged.playerAssignments = {
    ...(local.playerAssignments || {}),
    ...(remote.playerAssignments || {}),
  };

  return merged;
};

/** Save snapshot to cloud (upsert) */
export const saveSnapshotToCloud = async (
  snapshot: PlayerSnapshot
): Promise<void> => {
  const userId = await getPlayerStateId();
  if (!userId) {
    console.warn("playerState: Cannot save to cloud - not authenticated");
    return;
  }

  const playerStateData = {
    id: userId,
    gameList: snapshot.gameList ? JSON.stringify(snapshot.gameList) : null,
    playerAssignments: snapshot.playerAssignments
      ? JSON.stringify(snapshot.playerAssignments)
      : null,
    preferences: snapshot.preferences
      ? JSON.stringify(snapshot.preferences)
      : null,
  };

  try {
    // Try to fetch existing
    const existing: any = await API.graphql({
      query: queries.getPlayerState,
      variables: { id: userId },
      authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
    });

    if (existing.data.getPlayerState) {
      // Update
      await API.graphql({
        query: mutations.updatePlayerState,
        variables: { input: playerStateData },
        authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
      });
    } else {
      // Create
      await API.graphql({
        query: mutations.createPlayerState,
        variables: { input: playerStateData },
        authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
      });
    }
    console.log("playerState: Saved to cloud");
  } catch (error) {
    console.error("playerState: Failed to save to cloud", error);
  }
};

/** Get remote state from cloud */
export const getRemoteState = async (): Promise<PlayerStateRecord | null> => {
  const userId = await getPlayerStateId();
  if (!userId) return null;

  try {
    const result: any = await API.graphql({
      query: queries.getPlayerState,
      variables: { id: userId },
      authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
    });
    return result.data.getPlayerState || null;
  } catch (error) {
    console.error("playerState: Failed to get remote state", error);
    return null;
  }
};

/** Persist local state to cloud (backup current localStorage) */
export const persistLocalStateToCloud = async (): Promise<void> => {
  if (!(await isAuthenticated())) return;

  const localSnapshot = captureLocalSnapshot();
  await saveSnapshotToCloud(localSnapshot);
};

/** Restore state from cloud on login (merge with local) */
export const restoreLocalStateFromCloud = async (): Promise<void> => {
  if (!(await isAuthenticated())) return;

  const remoteState = await getRemoteState();
  if (!remoteState) {
    console.log("playerState: No remote state found, persisting local state");
    await persistLocalStateToCloud();
    return;
  }

  const remoteSnapshot: PlayerSnapshot = {};
  if (remoteState.gameList) {
    try {
      remoteSnapshot.gameList = JSON.parse(remoteState.gameList);
    } catch {}
  }
  if (remoteState.playerAssignments) {
    try {
      remoteSnapshot.playerAssignments = JSON.parse(
        remoteState.playerAssignments
      );
    } catch {}
  }

  const localSnapshot = captureLocalSnapshot();
  const mergedSnapshot = mergeSnapshots(localSnapshot, remoteSnapshot);

  applySnapshotToLocalStorage(mergedSnapshot);
  await saveSnapshotToCloud(mergedSnapshot);

  console.log("playerState: Restored and merged from cloud");
};

/**
 * Get player nickname from cloud
 */
export const getPlayerNickname = async (): Promise<string | null> => {
  try {
    const userId = await getPlayerStateId();
    if (!userId) return null;

    const result: any = await API.graphql({
      query: queries.getPlayerState,
      variables: { id: userId },
      authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
    });

    return result?.data?.getPlayerState?.playerNickname || null;
  } catch (error) {
    console.error("Failed to get player nickname:", error);
    return null;
  }
};

/**
 * Save player nickname to cloud
 */
export const savePlayerNickname = async (
  nickname: string
): Promise<boolean> => {
  try {
    const userId = await getPlayerStateId();
    if (!userId) {
      console.warn("Cannot save nickname: not authenticated");
      return false;
    }

    // Check if record exists
    const existing: any = await API.graphql({
      query: queries.getPlayerState,
      variables: { id: userId },
      authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
    });

    if (existing?.data?.getPlayerState) {
      // Update existing
      await API.graphql({
        query: mutations.updatePlayerState,
        variables: {
          input: {
            id: userId,
            playerNickname: nickname,
            _version: existing.data.getPlayerState._version,
          },
        },
        authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
      });
    } else {
      // Create new
      await API.graphql({
        query: mutations.createPlayerState,
        variables: {
          input: {
            id: userId,
            playerNickname: nickname,
          },
        },
        authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
      });
    }

    console.log("✅ Player nickname saved:", nickname);
    return true;
  } catch (error) {
    console.error("❌ Failed to save player nickname:", error);
    return false;
  }
};
